import { Autocomplete, Button, Grid, Paper, SnackbarContent, TextField, Typography, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Alert, CircularProgress } from "@mui/material";
import { Box, Stack } from "@mui/system";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useContext, useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SharedStateContext from "../../../../SharedStateProvider";
import { DesignThemeEndPointLogic } from "../../../../../logic/AssetManagement/Design/Theme/DesignThemeEndPointLogic";
import { selectUserToken, setDisplayErrorSnackBar, setIsLoading } from "../../../../../features/userSlice";
import { useSelector, useDispatch } from "react-redux";
import { DesignTemplatesEndPointLogic } from "../../../../../logic/AssetManagement/Design/Templates/DesignTemplatesEndPointLogic";
import AssignTemplates from "./AssignTemplates";
import store from "../../../../../store";
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import GenericEndPointsLogic from "../../../../../logic/GenericEndPointsLogic/GenericEndPointsLogic";

// Static entity type sent to the variants endpoint for this module
const VARIANTS_ENTITY_TYPE = "theme";

function CreateNewTheme() {
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Initialize the useNavigate hook
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [themeName, setThemeName] = useState("");
  const [dictionary, setDictionary] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false); // State for error message visibility
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedSharedUid = searchParams.get("selectedSite") as any; // -- Get the selected shared UID from the URL parameters
  const { designTemplatesDataTheme, siteOptions, designThemesData, widgetStructures } = useSelector((state: any) => state?.theme);
  const [selectedTemplates, setSelectedTemplates] = useState<Record<string, any>>({});
  const [, setJsonObject] = useState({});
  const { handleCreateClose } = useContext(SharedStateContext);

  useEffect(() => {
    if (selectedSharedUid) {
      const fetchTemplate = async () => {
        try {
          setLoading(true);
          await Promise.all([
            DesignTemplatesEndPointLogic.getDesignTemplatesTheme(oauthTokenHeader, selectedSharedUid, dispatch),
            DesignThemeEndPointLogic.getAllWidgetStructures(oauthTokenHeader),
          ]);
        } catch (error) {
          console.error("Error fetching templates:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchTemplate();
    }
    // eslint-disable-next-line
  }, [selectedSharedUid]);

  const selectedTemplateNames = Object.keys(selectedTemplates).filter((key) => selectedTemplates[key] !== null && selectedTemplates[key] !== undefined);
  const selectedTemplatesids = Object.values(selectedTemplates).filter((templateId) => templateId !== null && templateId !== undefined);

  const createTheme = async () => {
    const data = {
      website_shared_uid: selectedSharedUid,
      name: themeName,
      dictionary: dictionary ? dictionary : "{}",
      templates: selectedTemplatesids.filter((item) => item !== null && item !== undefined && !(Array.isArray(item) && item.length === 0)), // Filter out null, undefined, "", and []
    };
    try {
      if (oauthTokenHeader) {
        const createResponse = await DesignThemeEndPointLogic.createDesignTheme(oauthTokenHeader, data);
        // Create response returns the new theme's shared_uid directly in `data`
        const themeSharedUid = createResponse?.data?.data;
        if (typeof themeSharedUid === "string" && themeSharedUid) {
          GenericEndPointsLogic.updateVariants({
            oauthTokenHeader,
            websiteSharedUid: selectedSharedUid,
            entityType: VARIANTS_ENTITY_TYPE,
            entitySharedUid: themeSharedUid,
          });
        }
      } else {
        throw new Error("User doesn't have permissions to create new themes");
      }
      setDialogOpen(true);
      setIsLoading(true);
      await DesignThemeEndPointLogic.getDesignThemesData(oauthTokenHeader, selectedSharedUid, dispatch);
    } catch (error: any) {
      store.dispatch(setDisplayErrorSnackBar({ message: error?.parsedText || "Error while fetching data. Please try again later.", type: "error", }));
    } finally {
      setIsLoading(false);
    }
  };

  const validate = async () => {
    // Check if all mandatory fields are filled
    if (!themeName || !selectedTemplateNames.includes("Page Structure")) {
      setSnackbarOpen(true);
      setSnackbarMessage("Please fill in all the mandatory fields");
      setIsValid(false);
      return false;
    }

    // Check if themeName is at least 8 characters long and contains only letters and numbers
    const themeNameRegex = /^[a-zA-Z0-9-_&*$#!:|,./\\ ]{8,}$/;
    if (!themeNameRegex.test(themeName)) {
      setSnackbarOpen(true);
      setSnackbarMessage(
        "Template name must be at least 8 characters long and must contain only letters, numbers underscores and hyphens"
      );
      setIsValid(false);
      return false;
    }

    const isThemeNameUnique = designThemesData?.every(
      (item: any) => item.name !== themeName
    );

    if (!isThemeNameUnique) {
      setSnackbarOpen(true);
      setSnackbarMessage("Theme name must be unique");
      setIsValid(false);
      return false;
    }

    try {
      if (dictionary) {
        const cleanedDictionary = dictionary.replace(/\\n|\\+/g, ""); // Remove extra backslashes and newline characters
        const parsedAdditionalInfo = JSON.parse(cleanedDictionary);
        if (typeof parsedAdditionalInfo !== "object") {
          throw new Error("Not an object");
        }
      }
    } catch (e) {
      setSnackbarOpen(true);
      setSnackbarMessage("Dictionary must be a valid JSON object");
      setShowErrorMessage(true);
      setIsValid(false);
      return false;
    }

    setIsValid(true); // Validation passed
    return true; // Validation passed
  };

  const handleCreateTheme = async () => {
    const isFormValid = await validate();
    if (!isFormValid) return; // Exit if form is invalid
    try {
      setLoading(true);
      await createTheme(); // call the createTheme function
      setIsLoading(true);
      setDialogOpen(true);
    } catch (error) {
      console.error(error, "error");
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    handleCreateClose();
    navigate(`/operations/theme${location.search}`); // Redirect to the specified route
  };

  const handleClose = () => {
    setDialogOpen(false);
    handleCreateClose(); // Close the dialog
    setThemeName(""); // Reset the themeName
    setDictionary(""); // Reset the dictionary
    setSelectedTemplates([]); // Reset the selectedTemplates
    navigate(`/operations/theme${location.search}`); // Redirect to the specified route
  };

  const formatJSON = () => {
    try {
      const parsedJSON = JSON.parse(dictionary.trim() || "{}");
      const formattedJSON = JSON.stringify(parsedJSON, null, 2);
      setDictionary(formattedJSON);
      setShowErrorMessage(false);
    } catch (error) {
      setShowErrorMessage(true);
    }
  };

  useEffect(() => {
    if (dictionary) {
      let value = dictionary;
      value = value
        ?.replace(/\\n/g, "")
        ?.replace(/\\\\/g, "")
        ?.replace(/\\"/g, '"'); // Remove "\n", "\\" and extra backslashes
      if (value?.startsWith('"') && value?.endsWith('"')) {
        value = value?.slice(1, -1); // Remove the first and last character
      }
      try {
        const parsedJSON = JSON.parse(value || "{}");
        setJsonObject(parsedJSON);
        setShowErrorMessage(false);
      } catch (error) {
        setShowErrorMessage(true);
      }
    } else {
      setJsonObject({});
      setShowErrorMessage(false);
    }
  }, [dictionary])

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMessage.includes("Error") || isValid ? "success" : "error"
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  return (
    <>
      {" "}
      {renderSnackbar()}
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Paper
        sx={{
          padding: "8px 16px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography fontSize={24}>Create New Theme</Typography>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <Stack>
                <Typography marginTop={1}>General</Typography>
                <TextField
                  id="outlined-multiline-flexible"
                  label="Theme Name" // Set your label here
                  placeholder="How you like the Theme to be called" // Set your placeholder here
                  multiline
                  required
                  color="secondary"
                  size="small"
                  maxRows={4}
                  value={themeName}
                  onChange={(e) => {
                    setThemeName(e.target.value);
                  }}
                  sx={{
                    width: "100%",
                    marginTop: "16px",
                  }}
                />
                <Typography fontSize={12} marginTop={0.5}>
                  minimum of 8 characters, only letters and numbers.{" "}
                </Typography>
                <Autocomplete
                  sx={{ width: "100%", marginTop: "16px" }}
                  options={siteOptions}
                  getOptionLabel={(option) => option.siteName}
                  value={
                    siteOptions.find(
                      (option: any) => option.uid === selectedSharedUid
                    ) || null
                  }
                  disabled
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Related website"
                      InputProps={{
                        ...params.InputProps,
                      }}
                      color="secondary"
                      required
                    />
                  )}
                  size="small"
                />
              </Stack>
            </Grid>
            <Grid item xs={8}>
              <Stack flexDirection={"row"} gap={4}>
                <Grid item xs={6}>
                  <Stack>
                    <Typography marginTop={1} marginBottom={2}>
                      Code
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                      <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1 }}>
                        <AceEditor
                          mode="json"
                          theme="dark" // or "monokai" for dark theme
                          name="json-dictionary-editor"
                          onChange={(value) => setDictionary(value)}
                          value={dictionary}
                          fontSize={12}
                          showPrintMargin={false}
                          showGutter={false}
                          highlightActiveLine={false}
                          width="100%"
                          height="240px"
                          setOptions={{
                            enableBasicAutocompletion: true,
                            enableLiveAutocompletion: true,
                            enableSnippets: true,
                            tabSize: 2,
                            useWorker: false, // Important: prevents console errors
                            wrap: true,
                            fontSize: 14,
                            fontFamily: 'Geist Mono, monospace', // Use a monospaced font
                            showLineNumbers: false
                          }}
                          editorProps={{
                            $blockScrolling: true
                          }}
                          style={{
                            borderRadius: '4px',
                          }}
                        />
                        {dictionary && showErrorMessage === true ? (
                          <Typography
                            fontSize={12}
                            marginTop={2}
                            color={"#D32F2F"}
                          >
                            Invalid JSON format. Please correct the JSON syntax.
                          </Typography>
                        ) : null}
                      </Box>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        sx={{ height: '100%' }}
                        disabled={!dictionary}
                        onClick={formatJSON}
                      >
                        Format
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack mt={2}>
                    <SnackbarContent
                      sx={{
                        background: "#FDEDED",
                        color: "#5F2120",
                        marginTop: "32px",
                        boxShadow: "none",
                        border: "1px solid #D32F2F80",
                        padding: "0 4px",
                      }}
                      message={
                        <Box display="flex">
                          <ErrorOutlineIcon
                            sx={{ marginRight: "8px", color: "#5F2120" }}
                          />
                          <Box>
                            <Typography
                              variant="body1"
                              fontWeight="500"
                              sx={{ marginBottom: "4px" }}
                            >
                              Update with Caution:
                            </Typography>
                            <Typography variant="body2">
                              Please proceed with extreme care when making
                              updates, as these changes may adversely affect the
                              overall design functionality. Ensure thorough
                              testing and validation to avoid any unintended
                              disruptions or errors in the end user experience.
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Stack>
                </Grid>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography>Assign Templates to Objects</Typography>
          <AssignTemplates widgetStructures={widgetStructures} designTemplatesDataTheme={designTemplatesDataTheme} selectedTemplates={selectedTemplates} setSelectedTemplates={setSelectedTemplates} />
        </Box>
        {/* will not scroll the button */}
        <Box>
          <Stack
            flexDirection={"row"}
            justifyContent={"end"}
            marginTop={4}
            gap={1}
          >
            <Button color="secondary" variant="outlined">
              <Stack flexDirection={"row"} gap={1} onClick={handleCancelClick}>
                <ArrowBackIosNewIcon fontSize="small" />
                Cancel
              </Stack>
            </Button>
            <Button
              data-tracker="141"
              variant="contained"
              color="secondary"
              disabled={!themeName || !selectedTemplateNames.includes("Page Structure") || showErrorMessage}
              onClick={handleCreateTheme}
            >
              <Stack flexDirection={"row"} gap={1}>
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Create Theme"
                )}
                <CheckIcon fontSize="small" />
              </Stack>
            </Button>
          </Stack>
        </Box>
      </Paper>
      )}
      {/* Dialog component */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogContent sx={{ padding: "0px 0px 16px 0px" }}>
            <DialogTitle>
              <Typography variant="h6">Theme Created Successfully!</Typography>
            </DialogTitle>
          </DialogContent>
          <DialogActions>
            <Button
              color="secondary"
              onClick={handleClose}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
    </>
  );
}

export default CreateNewTheme;
